// src/pages/PagesUtilisateurs/DashboardUsers/modules/FavoriteEventsDisplay.jsx

import React, {
  useEffect,
  useMemo,
  useState,
} from "react";

import { FaCalendarAlt } from "react-icons/fa";

import { getUsersEvents } from "../../../../services/usersEventsServices";
import { getAllEvents } from "../../../../services/eventServices";

import { toAbsoluteEventPhotoUrl } from "../../../../services/eventPhotoServices";

import DashboardEmptyState from "../ui/DashboardEmptyState";

import {
  formatDateTime,
  getEventId,
  uniqueIds,
} from "../utils/dashboardUser.utils";

export default function FavoriteEventsDisplay({
  userId,
  onCounts,
}) {
  const [events, setEvents] =
    useState([]);

  const [favoriteIds, setFavoriteIds] =
    useState([]);

  const [loading, setLoading] =
    useState(true);

  useEffect(() => {
    let mounted = true;

    const load = async () => {
      if (!userId) return;

      try {
        setLoading(true);

        const [
          favoritesData,
          eventsData,
        ] = await Promise.all([
          getUsersEvents(
            userId,
          ).catch(() => []),

          getAllEvents().catch(
            () => [],
          ),
        ]);

        if (!mounted) return;

        const ids = uniqueIds(
          (
            Array.isArray(
              favoritesData,
            )
              ? favoritesData
              : []
          ).map(getEventId),
        );

        setFavoriteIds(ids);

        setEvents(
          Array.isArray(eventsData)
            ? eventsData
            : [],
        );

        onCounts?.({
          total: ids.length,
        });
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    load();

    return () => {
      mounted = false;
    };
  }, [userId, onCounts]);

  const favorites = useMemo(() => {
    const map = new Map(
      events.map((event) => [
        Number(event.id),
        event,
      ]),
    );

    return favoriteIds
      .map((id) =>
        map.get(Number(id)),
      )
      .filter(Boolean);
  }, [events, favoriteIds]);

  if (loading) {
    return (
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
        {Array.from({
          length: 4,
        }).map((_, index) => (
          <div
            key={index}
            className="h-28 animate-pulse rounded-2xl bg-slate-100 dark:bg-slate-800"
          />
        ))}
      </div>
    );
  }

  if (!favorites.length) {
    return (
      <DashboardEmptyState
        icon={FaCalendarAlt}
        title="Aucun évènement favori"
        description="Les évènements que vous enregistrez apparaîtront ici."
      />
    );
  }

  return (
    <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
      {favorites.map((event) => {
        const title =
          event.titre ||
          event.title ||
          event.nom ||
          "Évènement";

        const dateValue =
          event.date ||
          event.date_event ||
          event.debut ||
          event.start_date ||
          event.start;

        const {
          date,
        } = formatDateTime(
          dateValue,
        );

        const location =
          event.lieu_event ||
          event.location ||
          event.ville ||
          event.place;

        const image =
          event.photo_url ||
          event.affiche_url ||
          event.photo ||
          event.cover;

        return (
          <article
            key={event.id}
            className="flex gap-3 rounded-2xl border border-slate-200 bg-white p-3 transition hover:border-slate-300 hover:shadow-sm dark:border-slate-800 dark:bg-slate-900"
          >
            <img
              src={
                toAbsoluteEventPhotoUrl(
                  image,
                ) ||
                "/img/events/placeholder.jpg"
              }
              alt={title}
              className="h-20 w-20 shrink-0 rounded-xl object-cover"
              onError={(eventImage) => {
                eventImage.currentTarget.src =
                  "/img/events/placeholder.jpg";
              }}
            />

            <div className="min-w-0">
              <h4 className="line-clamp-2 font-bold text-slate-950 dark:text-white">
                {title}
              </h4>

              <p className="mt-2 text-xs font-semibold text-slate-500 dark:text-slate-400">
                {date}
              </p>

              {location ? (
                <p className="mt-1 line-clamp-1 text-xs text-slate-400">
                  {location}
                </p>
              ) : null}
            </div>
          </article>
        );
      })}
    </div>
  );
}